// An exam edited on the Exams page (RFC-048, byj-1wv.56.8): its public part
// — title, agents, limits, rubric, each question's mode, prompt, hand-in and
// bar — saved as a draft the instructor's machine lays over the kit
// (`byj exam pull KIT`), checks there, where the hidden part is, and says
// back: each check and whether it held, what is wrong, what was published.
// Edited where it is read (byj-1wv.51.12): the exam's page shows each part,
// and its Edit opens that part alone in a short form over the page
// (components/FormDialog) — its settings, its scoring, one question — saved
// to the draft as it closes. What a field decides is in its unit or
// placeholder; the little more a part needs is one line, and the rest folded.
import { useState } from "react";
import type { ExamDraft, ExamDraftBody, ExamMeta, ExamQuestion } from "../api";
import { barWords } from "../exams";
import { t, whenMinute, word } from "../i18n";
import { FormDialog } from "./FormDialog";

const kinds = ["byj", "claude", "codex"];

/** The draft to start from: the one saved, else the version published. */
export function draftFrom(meta: ExamMeta): ExamDraftBody {
  return {
    title: meta.title,
    agents: [...meta.agents],
    time_limit_min: meta.time_limit_min,
    attempts: meta.attempts,
    rubric: meta.rubric ? JSON.parse(JSON.stringify(meta.rubric)) : undefined,
    questions: meta.questions.map((q) => ({ ...q, criteria: q.criteria ? { ...q.criteria } : undefined })),
  };
}

/** The rubric an exam starts with (RFC-048 "the default rubric"). */
export const defaultRubric = (): NonNullable<ExamDraftBody["rubric"]> => ({
  accuracy: 40,
  agent: 15,
  efficiency: { "tool-calls": 10, "cost-usd": 10, seconds: 5 },
  process: 20,
  "zero-at": 2,
});

/** A new exam's first draft: its title, Claude Code, one question, the default rubric. */
export function blankDraft(title: string): ExamDraftBody {
  return {
    title,
    agents: ["claude"],
    attempts: 1,
    rubric: defaultRubric(),
    questions: [{ id: "q1", mode: "vibe", prompt: "", submit: ["answer.txt"] }],
  };
}

/** The rubric's points in all. */
export function rubricTotal(r: ExamDraftBody["rubric"]): number {
  if (!r) return 0;
  return (r.accuracy ?? 0) + (r.agent ?? 0) + Object.values(r.efficiency ?? {}).reduce((n, v) => n + (v ?? 0), 0) + (r.process ?? 0);
}

const num = (v: string): number | undefined => (v.trim() === "" ? undefined : Number(v));

/** A number with its unit after it, the unit saying what it counts. */
function NumberField({ label, value, onChange, unit, step }: { label: string; value?: number; onChange: (v?: number) => void; unit?: string; step?: string }) {
  return (
    <label className="small number-field">
      {label}
      <span className="row">
        <input type="number" min="0" step={step ?? "1"} value={value ?? ""} onChange={(e) => onChange(num(e.target.value))} />
        {unit && <span className="muted">{unit}</span>}
      </span>
    </label>
  );
}

/** Who sits it and for how long: its title, the agents, the time limit, the hand-ins allowed. */
export function SettingsDialog({ body, onSave, onClose }: { body: ExamDraftBody; onSave: (b: ExamDraftBody) => Promise<void>; onClose: () => void }) {
  const [b, setB] = useState(body);
  return (
    <FormDialog id="exam-settings" title={t("exams:editorStepBasics")} now={t("exams:settingsLead")} submitLabel={t("common:save")} canSubmit onSubmit={() => onSave(b).then(onClose)} onClose={onClose} testid="settings-dialog">
      <label className="field">
        {t("exams:editorTitleLabel")}
        <input value={b.title} onChange={(e) => setB({ ...b, title: e.target.value })} maxLength={200} />
      </label>
      <fieldset className="plain-fieldset">
        <legend className="small">{t("exams:editorAgents")}</legend>
        <div className="row wrap">
          {kinds.map((k) => (
            <label key={k} className="toggle small">
              <input type="checkbox" checked={b.agents.includes(k)} onChange={(e) => setB({ ...b, agents: e.target.checked ? [...b.agents, k] : b.agents.filter((x) => x !== k) })} />
              {k}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="row wrap edit-numbers">
        <NumberField label={t("exams:editorTimeLimit")} unit={t("exams:unitLimitMinutes")} value={b.time_limit_min} onChange={(v) => setB({ ...b, time_limit_min: v })} />
        <NumberField label={t("exams:editorAttempts")} unit={t("exams:unitPerQuestion")} value={b.attempts} onChange={(v) => setB({ ...b, attempts: v })} />
      </div>
    </FormDialog>
  );
}

/**
 * How it is scored: the four parts' points and their total, the default a
 * press away; efficiency's split by tool calls, cost and time, and what
 * earns each part, folded.
 */
export function ScoringDialog({ body, onSave, onClose }: { body: ExamDraftBody; onSave: (b: ExamDraftBody) => Promise<void>; onClose: () => void }) {
  const [r, setR] = useState<NonNullable<ExamDraftBody["rubric"]>>(body.rubric ?? defaultRubric());
  const set = (k: string, v?: number) => setR({ ...r, [k]: v });
  const setEff = (k: string, v?: number) => setR({ ...r, efficiency: { ...(r.efficiency ?? {}), [k]: v ?? 0 } });
  const eff = Object.values(r.efficiency ?? {}).reduce<number>((n, v) => n + (v ?? 0), 0);
  const total = rubricTotal(r);
  return (
    <FormDialog
      id="exam-scoring"
      title={t("exams:editorStepScoring")}
      now={t("exams:editorScoringHelp")}
      submitLabel={t("common:save")}
      canSubmit
      onSubmit={() => onSave({ ...body, rubric: r }).then(onClose)}
      onClose={onClose}
      testid="scoring-dialog"
    >
      <div className="row">
        <button type="button" className="small" onClick={() => setR(defaultRubric())}>
          {t("exams:useDefaultRubric")}
        </button>
      </div>
      <div className="edit-numbers rubric-fields" data-testid="editor-rubric">
        <NumberField label={t("exams:rubricPart.accuracy")!} value={r.accuracy} onChange={(v) => set("accuracy", v)} />
        <NumberField label={t("exams:rubricPart.agent")!} value={r.agent} onChange={(v) => set("agent", v)} />
        <label className="small number-field">
          {t("exams:rubricPart.efficiency")}
          <output className="rubric-sum">{eff}</output>
        </label>
        <NumberField label={t("exams:rubricPart.process")!} value={r.process} onChange={(v) => set("process", v)} />
      </div>
      <details className="edit-more">
        <summary className="small">{t("exams:effByPart")}</summary>
        <div className="edit-numbers">
          {["tool-calls", "cost-usd", "seconds"].map((k) => (
            <NumberField key={k} label={word("exams:effLabel", k) ?? k} value={r.efficiency?.[k]} onChange={(v) => setEff(k, v)} />
          ))}
        </div>
      </details>
      <p className={`small flush rubric-total ${total === 100 ? "muted" : "error"}`}>
        {t("exams:rubricTotal", { n: total })}
        {total !== 100 && ` — ${t("exams:rubricNot100")}`}
      </p>
      <details className="edit-more">
        <summary className="small">{t("exams:whatEarnsEach")}</summary>
        <ul className="small muted flush rubric-meanings">
          {(["accuracy", "agent", "efficiency", "process"] as const).map((k) => (
            <li key={k}>
              <strong>{word("exams:rubricPart", k)}</strong> — {word("exams:rubricHelp", k)}
            </li>
          ))}
        </ul>
      </details>
    </FormDialog>
  );
}

/**
 * One question: what it asks, how it is sat — its mode a choice of two,
 * the one picked said in a line — and what it hands in; its budgets and
 * weight folded, their summary on the fold's line. A question new here
 * names its own id, and its hidden part is still to make in the kit.
 * Remove is the page's, which asks first (`onRemove`).
 */
export function QuestionDialog({ q, isNew, taken, onSave, onRemove, onClose }: {
  q: ExamQuestion;
  isNew: boolean;
  /** the other questions' ids, which a new one may not take */
  taken: string[];
  onSave: (q: ExamQuestion) => Promise<void>;
  onRemove?: () => void;
  onClose: () => void;
}) {
  const [x, setX] = useState(q);
  const c = x.criteria ?? {};
  const setC = (k: string, v: string | number | undefined) => setX({ ...x, criteria: { ...c, [k]: v === "" ? undefined : v } });
  const bar = barWords(x.criteria);
  const save = async () => {
    if (!x.id.trim()) throw new Error(t("exams:questionIdNeeded"));
    if (isNew && taken.includes(x.id.trim())) throw new Error(t("exams:questionIdTaken", { id: x.id.trim() }));
    await onSave({ ...x, id: x.id.trim() });
    onClose();
  };
  return (
    <FormDialog
      id="exam-question"
      title={isNew ? t("exams:newQuestionTitle") : t("exams:questionTitle", { id: q.id })}
      now={isNew ? t("exams:editorNewNote") : undefined}
      submitLabel={t("common:save")}
      canSubmit
      onSubmit={save}
      onClose={onClose}
      testid="question-dialog"
    >
      {isNew && (
        <label className="field">
          {t("exams:editorQuestionId")}
          <input value={x.id} onChange={(e) => setX({ ...x, id: e.target.value })} placeholder="q2-free-delivery" />
        </label>
      )}
      <label className="field">
        {t("exams:editorPrompt")}
        <textarea rows={4} value={x.prompt} onChange={(e) => setX({ ...x, prompt: e.target.value })} />
      </label>
      <fieldset className="plain-fieldset">
        <legend className="small">{t("exams:editorMode")}</legend>
        <div className="segmented" role="radiogroup" aria-label={t("exams:editorMode")}>
          {(["vibe", "agent-build"] as const).map((m) => (
            <label key={m} className={`segment${x.mode === m ? " picked" : ""}`}>
              <input type="radio" name="question-mode" value={m} checked={x.mode === m} onChange={() => setX({ ...x, mode: m })} />
              {word("exams:questionMode", m)}
            </label>
          ))}
        </div>
        <p className="muted small flush">{word("exams:modeHelp", x.mode)}</p>
      </fieldset>
      <label className="field">
        {t("exams:editorSubmit")}
        <input
          value={(x.submit ?? []).join(", ")}
          placeholder={t("exams:submitPlaceholder")}
          onChange={(e) => setX({ ...x, submit: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
        />
      </label>
      <details className="edit-more" data-testid="question-budgets">
        <summary className="small">
          {t("exams:budgetsOptional")} <span className="muted">— {bar || t("exams:budgetsNone")}</span>
        </summary>
        <p className="muted small flush">{t("exams:editorBudgetsHelp")}</p>
        <label className="field">
          {t("exams:editorAgent")}
          <input value={c.agent ?? ""} onChange={(e) => setC("agent", e.target.value)} placeholder="pricing-analyst" />
        </label>
        <div className="edit-numbers">
          <NumberField label={word("exams:effLabel", "tool-calls")!} unit={t("exams:unitAtMost")} value={c["max-tool-calls"]} onChange={(v) => setC("max-tool-calls", v)} />
          <NumberField label={word("exams:effLabel", "cost-usd")!} unit="$" step="0.01" value={c["max-cost-usd"]} onChange={(v) => setC("max-cost-usd", v)} />
          <NumberField label={t("exams:effLabel.seconds")!} unit="s" value={c["max-seconds"]} onChange={(v) => setC("max-seconds", v)} />
          <NumberField label={t("exams:editorWeight")} unit="×" value={c.weight} onChange={(v) => setC("weight", v)} />
        </div>
      </details>
      {onRemove && (
        <div className="row">
          <button type="button" className="small danger" onClick={onRemove}>
            {t("exams:editorRemove")}
          </button>
        </div>
      )}
    </FormDialog>
  );
}

/** What the machine said of the draft: whether it held, problems, changes, publish — each question's check on its card (byj-1wv.51.12). */
export function DraftStatus({ draft }: { draft: ExamDraft }) {
  const st = draft.status;
  if (!st) return <p className="muted small" data-testid="draft-status">{t("exams:draftNotPulled")}</p>;
  return (
    <div className="stack small" data-testid="draft-status">
      <p className="flush">
        {t("exams:draftStatusTitle")} {whenMinute(new Date(st.pulled_at).toISOString())} —{" "}
        <span className={`chip ${st.ok ? "chip-live" : "chip-warn"}`}>{st.ok ? t("exams:draftValid") : t("exams:draftInvalid")}</span>
        {st.published_version != null && <span className="chip chip-live">{t("exams:draftPublished", { v: st.published_version })}</span>}
      </p>
      {st.draft_updated_at !== draft.updated_at && <p className="muted flush">{t("exams:draftStale")}</p>}
      {st.problems.length > 0 && (
        <ul className="flush" data-testid="draft-problems">
          {st.problems.map((p) => (
            <li key={p} className="error">
              {p}
            </li>
          ))}
        </ul>
      )}
      {st.changes.length > 0 && (
        <details>
          <summary>{t("exams:draftChanges")}</summary>
          <ul className="flush">
            {st.changes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
